import Breadcrumbs from "@/components/Breadcrumbs";
import { SettingsService } from "@/services/admin-services";
import { SettingsFooterLink } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../..",
    },
    {
        title: "Footer Links",
        to: "",
    },
];

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<Array<SettingsFooterLink>>([]);
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const settingsService = useMemo(() => new SettingsService("int"), []);

    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([LocaleService.get().then(parseLanguages), settingsService.getFooterLinks().then(setData)]).then(
            () => setIsLoading(false)
        );
    }, [settingsService]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Count
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 200 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    languages
                                        .sort((a, b) => a.label.localeCompare(b.label))
                                        .map((item, i) => {
                                            const links = data.filter((x) => x.language === item.slug);

                                            return (
                                                <TableRow
                                                    key={i}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {item.label}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell scope="row">
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {links.length}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell scope="row" sx={{ p: 0 }}>
                                                        <Box sx={{ display: "flex" }}>
                                                            <Button
                                                                color={"primary"}
                                                                sx={{ ml: "auto", mr: 1 }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/admin/settings/footer-links/${item.slug}`
                                                                    )
                                                                }
                                                            >
                                                                Edit
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
